import { ModuleDescriptor } from "./moduleLoader";

type FederationManifestEntry = {
  readonly metadataPath?: string;
  readonly enabled?: boolean;
  readonly federation: {
    readonly remoteEntry: string;
    readonly scope?: string;
    readonly exposedModule?: string;
  };
};

/*
  Pre-federation manifests mapped a module id straight to its bundle URL. The
  deployed manifest is written by a different release unit than the shell, so
  both shapes have to load until every environment has the federation manifest.
*/
type LegacyManifestEntry = string;

type ModuleManifestEntry = FederationManifestEntry | LegacyManifestEntry;

interface ManifestFetchDiagnosticDetail {
  readonly set: "core" | "app";
  readonly stage: "manifest-fetch";
  readonly status: "start" | "success" | "failure";
  readonly message?: string;
  readonly buildEpoch?: string;
  readonly timestamp: string;
}

function setBuildEpochFromHeaders(response: Response): void {
  const buildEpoch = response.headers.get("x-caprx-build-count");
  if (!buildEpoch) return;

  const hostGlobal = globalThis as typeof globalThis & {
    __CAPRX_BUILD_EPOCH__?: string;
  };

  hostGlobal.__CAPRX_BUILD_EPOCH__ = buildEpoch;
}

function emitManifestFetchDiagnostic(
  set: "core" | "app",
  status: ManifestFetchDiagnosticDetail["status"],
  message?: string,
): void {
  if (typeof window === "undefined") return;

  const hostGlobal = globalThis as typeof globalThis & {
    __CAPRX_BUILD_EPOCH__?: string;
  };

  globalThis.dispatchEvent(
    new CustomEvent("caprx:shell-module-diagnostic", {
      detail: {
        set,
        stage: "manifest-fetch",
        status,
        message,
        buildEpoch: hostGlobal.__CAPRX_BUILD_EPOCH__,
        timestamp: new Date().toISOString(),
      } as ManifestFetchDiagnosticDetail,
    }),
  );
}

/**
 * Replaces protocol/hostname placeholders in manifest paths with the current browser location values.
 * @param path A templated path that may include {{protocol}} and {{hostname}} placeholders.
 */
function replacePath(path: string): string {
  const { protocol, hostname } = globalThis.location;
  return path.replace("{{protocol}}", protocol).replace("{{hostname}}", hostname);
}

/** Derives the federation scope used to look up the remote container on `window`. */
function deriveScope(id: string): string {
  return id.split("-").join("_");
}

/*
  A legacy entry names the bundle, not the remote, so the sibling remoteEntry.js
  is the only thing we can point at. Metadata sits next to it, which is where the
  pre-federation shell always looked for it.
*/
function createLegacyModuleDescriptor(id: string, modulePath: string): ModuleDescriptor {
  const resolved = replacePath(modulePath);
  const basePath = resolved.replace(/[^/]*$/, "");

  return {
    id,
    metadataPath: `${basePath}metadata.json`,
    federation: {
      remoteEntry: `${basePath}remoteEntry.js`,
      scope: deriveScope(id),
    },
  };
}

/**
 * Converts a raw manifest entry into a normalized module load descriptor used by the shell loader.
 * @param id The manifest key for the module.
 * @param entry The module's raw manifest configuration.
 */
function createModuleDescriptor(id: string, entry: ModuleManifestEntry): ModuleDescriptor {
  if (typeof entry === "string") {
    return createLegacyModuleDescriptor(id, entry);
  }

  if (!entry.federation) {
    throw new Error(`Module [${id}] is missing federation configuration.`);
  }

  return {
    id,
    metadataPath: entry.metadataPath ? replacePath(entry.metadataPath) : undefined,
    federation: {
      remoteEntry: replacePath(entry.federation.remoteEntry),
      scope: entry.federation.scope || deriveScope(id),
      exposedModule: entry.federation.exposedModule,
    },
  };
}

/**
 * Parses a manifest object into a list of module descriptors.
 * @param entries Manifest entries keyed by module id.
 */
function parseManifestEntries(entries: Record<string, ModuleManifestEntry>): ReadonlyArray<ModuleDescriptor> {
  const descriptors: ModuleDescriptor[] = [];

  for (const [id, entry] of Object.entries(entries)) {
    /* An explicit `enabled: false` opts a module out of loading; omitted/true entries load normally. */
    if (typeof entry === "object" && entry !== null && entry.enabled === false) {
      continue;
    }

    try {
      descriptors.push(createModuleDescriptor(id, entry));
    } catch (err) {
      console.warn(`Skipping malformed module manifest entry [${id}].`, err);
    }
  }

  return descriptors;
}

/**
 * Fetches the set of available modules.
 * @param set The set of modules to load. This can be "core" for core modules only or "app" for application modules.
 * @returns The parsed module descriptors, or null when the manifest could not be read.
 */
export async function fetchAvailableModules(set: "core" | "app"): Promise<ReadonlyArray<ModuleDescriptor> | null> {
  try {
    emitManifestFetchDiagnostic(set, "start");

    const response = await fetch(`/modules/module_manifest.${set}.json`, {
      cache: "no-store",
    });

    setBuildEpochFromHeaders(response);

    /* A history-fallback 404 returns index.html, which would otherwise parse as an empty manifest. */
    if (!response.ok) {
      throw new Error(`Module manifest request for [${set}] failed with status ${response.status}.`);
    }

    const modules = (await response.json()) as Record<string, ModuleManifestEntry>;
    let descriptors = parseManifestEntries(modules);

    if (process.env.NODE_ENV === "development") {
      if (set === "app") {
        try {
          const devResponse = await fetch("/modules/module_manifest.dev.json", {
            cache: "no-store",
          });

          setBuildEpochFromHeaders(devResponse);

          const devModules = (await devResponse.json()) as Record<string, ModuleManifestEntry>;
          const userDescriptors = parseManifestEntries(devModules);
          descriptors = [...descriptors, ...userDescriptors];
        } catch {
          // no user modules, do nothing.
        }
      }
    }

    emitManifestFetchDiagnostic(set, "success");

    return descriptors;
  } catch (err) {
    emitManifestFetchDiagnostic(set, "failure", "Unable to fetch list of available modules.");
    console.warn("Unable to fetch list of available modules!", err);
    return null;
  }
}
